// ═══════════════════════════════════════════════════════════════
// CapacitySection — Capacity tab content for DonorDetailView
// Path: src/components/donorDetail/CapacitySection.jsx
//
// Phase 1 of 4 of the CapacityView blueprint.
// Observed signals + director-entered assessment only; no wealth
// import yet, no portfolio view yet. Confidence score silently
// rebalances when wealth data is absent.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo, useEffect } from "react";
import {
  Target,
  Edit2,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Info,
  Sparkles,
  Database,
  Search,
} from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { useApp } from "../../context/AppContext";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { InfoBubble } from "../ui/InfoBubble";
import { ManualResearchModal } from "./ManualResearchModal";
import {
  computeCapacityConfidence,
  CAPACITY_BANDS,
  INCLINATION_OPTIONS,
  DIRECTOR_CONFIDENCE_OPTIONS,
  ESTIMATE_SOURCE_OPTIONS,
  getCapacityBand,
} from "../../utils/capacityScoring";

// ── Helpers ──────────────────────────────────────────────────────
const formatMoney = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : "$0";

// Pick a color for a score band.
const scoreColor = (score) => {
  if (score >= 75) return "#10b981";
  if (score >= 50) return "#f59e0b";
  if (score >= 25) return "#ef4444";
  return "#94a3b8";
};

// ── Sub-components ───────────────────────────────────────────────

const ConfidenceDial = ({ score, palette }) => {
  const color = scoreColor(score);
  return (
    <div
      style={{
        width: 120,
        height: 120,
        borderRadius: "50%",
        background: `conic-gradient(${color} ${score * 3.6}deg, ${palette.cardBorder} 0deg)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 96,
          height: 96,
          borderRadius: "50%",
          background: palette.cardBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            fontSize: 28,
            fontWeight: 800,
            color: palette.text1,
            fontFamily: FONT_FAMILY,
            lineHeight: 1,
          }}
        >
          {score}%
        </div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: palette.text3,
            marginTop: 4,
            letterSpacing: 0.5,
          }}
        >
          CONFIDENCE
        </div>
      </div>
    </div>
  );
};

const ComponentBar = ({ component, palette }) => {
  const pct = component.score;
  const weight = Math.round(component.weight * 100);
  const color = scoreColor(pct);
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 5,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZES.sm,
            fontWeight: 600,
            color: palette.text1,
          }}
        >
          {component.label}
        </span>
        <span
          style={{
            fontSize: 11,
            color: palette.text3,
            fontFamily: FONT_FAMILY,
          }}
        >
          {pct} / 100 · weighted {weight}%
        </span>
      </div>
      <div
        style={{
          height: 8,
          borderRadius: 4,
          background: palette.cardBorder,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: color,
            transition: "width 0.3s ease",
          }}
        />
      </div>
      {component.missing && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 6,
            marginTop: 6,
            fontSize: 12,
            color: palette.text2,
            fontStyle: "italic",
          }}
        >
          <AlertCircle size={ICON_SIZES.xs} color="#f59e0b" style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{component.missing}</span>
        </div>
      )}
    </div>
  );
};

const StatTile = ({ label, value, palette }) => (
  <div
    style={{
      padding: "10px 14px",
      borderRadius: 8,
      background: `${palette.accent1}08`,
      border: `1px solid ${palette.cardBorder}`,
      minWidth: 110,
    }}
  >
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: palette.text3,
        letterSpacing: 0.3,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: FONT_SIZES.base,
        fontWeight: 700,
        color: palette.text1,
      }}
    >
      {value}
    </div>
  </div>
);

// ── Wealth profile block ────────────────────────────────────────
// Always renders on the Capacity tab. When no profile exists it
// shows a "Research this donor" CTA pointing at free public sources;
// when data exists (from an import OR manual research) it shows the
// fields and an Edit button that reopens the Research modal.

const SOURCE_LABELS = {
  donorsearch: "DonorSearch",
  iwave: "iWave",
  wealthengine: "WealthEngine",
  manual: "Manual research",
};

const SOURCE_BADGE_COLORS = {
  donorsearch: "#3b82f6",
  iwave: "#8b5cf6",
  wealthengine: "#0ea5e9",
  manual: "#10b981",
};

const WealthProfileBlock = ({ profile, palette, onEdit }) => {
  const fmtDollar = (n) =>
    n == null
      ? "—"
      : n.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        });
  const fmtImported = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  return (
    <Card>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 4,
        }}
      >
        <Database size={ICON_SIZES.sm} color={palette.accent1} />
        <div
          style={{
            fontSize: FONT_SIZES.base,
            fontWeight: 700,
            color: palette.text1,
          }}
        >
          Wealth Profile
        </div>
        <InfoBubble
          size={ICON_SIZES.sm}
          text="External wealth evidence for this donor — either imported from a vendor CSV or entered as manual public-record research. Contributes up to 15% of the confidence score by default."
        />
        <span
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 0.4,
              padding: "3px 8px",
              borderRadius: 99,
              background:
                (SOURCE_BADGE_COLORS[profile.source] || palette.accent1) +
                "18",
              color:
                SOURCE_BADGE_COLORS[profile.source] || palette.accent1,
            }}
          >
            {(SOURCE_LABELS[profile.source] || profile.source).toUpperCase()}
          </span>
          <span style={{ fontSize: 10, color: palette.text3 }}>
            · Updated {fmtImported(profile.importedAt)}
          </span>
          {onEdit && (
            <button
              onClick={onEdit}
              style={{
                background: "transparent",
                border: `1px solid ${palette.cardBorder}`,
                borderRadius: 6,
                padding: "4px 10px",
                cursor: "pointer",
                color: palette.text2,
                fontSize: 11,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
              }}
            >
              Edit research
            </button>
          )}
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: 10,
          marginTop: 12,
        }}
      >
        {profile.capacityRating && (
          <StatTile
            label="VENDOR RATING"
            value={profile.capacityRating}
            palette={palette}
          />
        )}
        {profile.capacityScore != null && (
          <StatTile
            label="VENDOR SCORE"
            value={String(profile.capacityScore)}
            palette={palette}
          />
        )}
        {profile.realEstateValue != null && (
          <StatTile
            label="REAL ESTATE"
            value={fmtDollar(profile.realEstateValue)}
            palette={palette}
          />
        )}
        {profile.stockValue != null && (
          <StatTile
            label="PUBLIC STOCK"
            value={fmtDollar(profile.stockValue)}
            palette={palette}
          />
        )}
        {profile.politicalGivingTotal != null && (
          <StatTile
            label="POLITICAL GIVING"
            value={fmtDollar(profile.politicalGivingTotal)}
            palette={palette}
          />
        )}
        {profile.foundationAffiliations?.length > 0 && (
          <StatTile
            label="FOUNDATIONS"
            value={String(profile.foundationAffiliations.length)}
            palette={palette}
          />
        )}
        {profile.businessOwnership?.length > 0 && (
          <StatTile
            label="BUSINESS ROLES"
            value={String(profile.businessOwnership.length)}
            palette={palette}
          />
        )}
      </div>
      {profile.foundationAffiliations?.length > 0 && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 8,
            background: `${palette.accent1}08`,
            border: `1px solid ${palette.cardBorder}`,
            fontSize: 12,
            color: palette.text2,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: palette.text1 }}>Foundations: </strong>
          {profile.foundationAffiliations
            .map((f) => f.name || f.raw || "—")
            .join(" · ")}
        </div>
      )}
      {profile.businessOwnership?.length > 0 && (
        <div
          style={{
            marginTop: 8,
            padding: 10,
            borderRadius: 8,
            background: `${palette.accent1}08`,
            border: `1px solid ${palette.cardBorder}`,
            fontSize: 12,
            color: palette.text2,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ color: palette.text1 }}>Business: </strong>
          {profile.businessOwnership
            .map((b) => b.company || b.raw || "—")
            .join(" · ")}
        </div>
      )}
    </Card>
  );
};

// ── Research Prompt (shown when no wealth profile exists yet) ───

const ResearchPromptBlock = ({ palette, onStart }) => (
  <Card>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 4,
      }}
    >
      <Search size={ICON_SIZES.sm} color={palette.accent1} />
      <div
        style={{
          fontSize: FONT_SIZES.base,
          fontWeight: 700,
          color: palette.text1,
        }}
      >
        Wealth Evidence
      </div>
      <InfoBubble
        size={ICON_SIZES.sm}
        text="No wealth data on file yet. You can enter public-record research manually — it counts for the same 15% of the confidence score as a DonorSearch import would."
      />
    </div>
    <p
      style={{
        color: palette.text2,
        fontSize: FONT_SIZES.sm,
        margin: "0 0 12px 0",
        lineHeight: 1.55,
      }}
    >
      Nothing on file for this donor yet. Fifteen minutes on public
      sources — real estate, SEC EDGAR, FEC.gov, 990-PF filings —
      will typically let you enter everything a paid wealth screen
      would have given you.
    </p>
    <ActionButton
      label="Research this donor"
      icon={Search}
      onClick={onStart}
    />
  </Card>
);

// ── Director's Assessment editor ─────────────────────────────────

const AssessmentEditor = ({ capacity, onSave, onCancel, palette, saving }) => {
  const [form, setForm] = useState({
    capacityBand: capacity?.capacityBand || "",
    inclination: capacity?.inclination || "",
    directorConfidence: capacity?.directorConfidence || "",
    estimateSource: capacity?.estimateSource || "",
    narrativeNotes: capacity?.narrativeNotes || "",
  });

  const labelStyle = {
    display: "block",
    fontSize: FONT_SIZES.xs,
    fontWeight: 700,
    color: palette.text2,
    marginBottom: 5,
    letterSpacing: 0.3,
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 7,
    fontSize: FONT_SIZES.sm,
    fontFamily: FONT_FAMILY,
    background: palette.inputBg,
    color: palette.inputColor,
    border: `1px solid ${palette.inputBorder}`,
    outline: "none",
    boxSizing: "border-box",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={labelStyle}>
            5-Year Capacity
            <InfoBubble
              size={ICON_SIZES.xs}
              text="The dollar range you believe this donor could give over 5 years if fully engaged. Pick the band that reflects realistic major-gift potential, not just current giving."
            />
          </label>
          <select
            value={form.capacityBand}
            onChange={(e) => setForm({ ...form, capacityBand: e.target.value })}
            style={inputStyle}
          >
            <option value="">— Not set —</option>
            {CAPACITY_BANDS.map((b) => (
              <option key={b.id} value={b.id}>{b.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>
            Inclination
            <InfoBubble
              size={ICON_SIZES.xs}
              text="How warm this donor is right now. Cold = no recent signal; Warm = engaged, not solicited; Hot = conversations happening; Ready = prepared to be asked."
            />
          </label>
          <select
            value={form.inclination}
            onChange={(e) => setForm({ ...form, inclination: e.target.value })}
            style={inputStyle}
          >
            <option value="">— Not set —</option>
            {INCLINATION_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>
            Your Confidence
            <InfoBubble
              size={ICON_SIZES.xs}
              text="How confident you are in the capacity band above. High = you've had conversations or seen hard evidence; Medium = observed signals; Low = educated guess."
            />
          </label>
          <select
            value={form.directorConfidence}
            onChange={(e) =>
              setForm({ ...form, directorConfidence: e.target.value })
            }
            style={inputStyle}
          >
            <option value="">— Not set —</option>
            {DIRECTOR_CONFIDENCE_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>
            Source of Estimate
            <InfoBubble
              size={ICON_SIZES.xs}
              text="Where your estimate comes from. Direct conversation is strongest; wealth screen or public-record research is second; director intuition is valid but should prompt follow-up."
            />
          </label>
          <select
            value={form.estimateSource}
            onChange={(e) =>
              setForm({ ...form, estimateSource: e.target.value })
            }
            style={inputStyle}
          >
            <option value="">— Not set —</option>
            {ESTIMATE_SOURCE_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label style={labelStyle}>
          What you know about this donor's capacity
        </label>
        <textarea
          rows={4}
          value={form.narrativeNotes}
          onChange={(e) =>
            setForm({ ...form, narrativeNotes: e.target.value })
          }
          placeholder="Observations, conversations, context — whatever helps justify the estimate."
          style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
        />
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <ActionButton
          label="Cancel"
          icon={X}
          variant="secondary"
          onClick={onCancel}
          disabled={saving}
        />
        <ActionButton
          label={saving ? "Saving..." : "Save Assessment"}
          icon={Save}
          onClick={() => onSave(form)}
          disabled={saving}
        />
      </div>
    </div>
  );
};

const AssessmentDisplay = ({ capacity, onEdit, palette }) => {
  const band = getCapacityBand(capacity?.capacityBand);
  const inclination = INCLINATION_OPTIONS.find(
    (o) => o.id === capacity?.inclination
  );
  const conf = DIRECTOR_CONFIDENCE_OPTIONS.find(
    (o) => o.id === capacity?.directorConfidence
  );
  const source = ESTIMATE_SOURCE_OPTIONS.find(
    (o) => o.id === capacity?.estimateSource
  );
  const hasAnything =
    capacity &&
    (capacity.capacityBand ||
      capacity.inclination ||
      capacity.directorConfidence ||
      (capacity.narrativeNotes || "").trim().length > 0);

  const row = {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    marginBottom: 6,
    fontSize: FONT_SIZES.sm,
  };

  if (!hasAnything) {
    return (
      <div
        style={{
          padding: 14,
          borderRadius: 8,
          background: `${palette.accent1}08`,
          border: `1px dashed ${palette.cardBorder}`,
        }}
      >
        <div
          style={{
            fontSize: FONT_SIZES.sm,
            color: palette.text2,
            marginBottom: 10,
          }}
        >
          Your assessment isn't on file yet. Your direct judgment counts
          for <strong>20%</strong> of this donor's confidence score.
        </div>
        <ActionButton
          label="Enter Capacity Assessment"
          icon={Edit2}
          onClick={onEdit}
        />
      </div>
    );
  }

  return (
    <div>
      <div style={row}>
        <span style={{ color: palette.text3, minWidth: 130 }}>5-Year Capacity:</span>
        <strong style={{ color: palette.text1 }}>
          {band ? band.label : "Not set"}
        </strong>
      </div>
      <div style={row}>
        <span style={{ color: palette.text3, minWidth: 130 }}>Inclination:</span>
        <strong style={{ color: inclination?.color || palette.text1 }}>
          {inclination ? inclination.label : "Not set"}
        </strong>
      </div>
      <div style={row}>
        <span style={{ color: palette.text3, minWidth: 130 }}>Your Confidence:</span>
        <strong style={{ color: palette.text1 }}>
          {conf ? conf.label : "Not set"}
        </strong>
      </div>
      <div style={row}>
        <span style={{ color: palette.text3, minWidth: 130 }}>Source:</span>
        <strong style={{ color: palette.text1 }}>
          {source ? source.label : "Not set"}
        </strong>
      </div>
      {(capacity.narrativeNotes || "").trim() && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 8,
            background: `${palette.accent1}08`,
            border: `1px solid ${palette.cardBorder}`,
            fontSize: FONT_SIZES.sm,
            color: palette.text2,
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
          }}
        >
          {capacity.narrativeNotes}
        </div>
      )}
      <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
        <ActionButton
          label="Edit Assessment"
          icon={Edit2}
          variant="secondary"
          onClick={onEdit}
        />
      </div>
    </div>
  );
};

// ── Main CapacitySection ─────────────────────────────────────────

export const CapacitySection = ({ onLogContact, engagementScore }) => {
  const {
    palette,
    selectedMember,
    selectedMemberCapacity,
    upsertSelectedMemberCapacity,
    allGifts,
    allWealthProfiles,
    capacityWeights,
    showMessage,
  } = useApp();

  const wealthProfile = allWealthProfiles?.[selectedMember?.id] || null;
  const chapterHasWealthData =
    Object.keys(allWealthProfiles || {}).length > 0;

  const [editing, setEditing] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditing(false);
    setResearchOpen(false);
  }, [selectedMember?.id]);

  const memberGifts = useMemo(
    () =>
      (allGifts || [])
        .filter((g) => g.member_id === selectedMember?.id)
        .sort((a, b) =>
          (b.gift_date || "").localeCompare(a.gift_date || "")
        ),
    [allGifts, selectedMember?.id]
  );

  const giftStats = useMemo(() => {
    if (memberGifts.length === 0) {
      return {
        lifetime: 0,
        largest: 0,
        count: 0,
        years: 0,
        lastGiftDate: null,
      };
    }
    let lifetime = 0;
    let largest = 0;
    const giftYears = new Set();
    memberGifts.forEach((g) => {
      const amt = Number(g.amount) || 0;
      lifetime += amt;
      if (amt > largest) largest = amt;
      if (g.gift_date) {
        giftYears.add(new Date(g.gift_date + "T12:00:00").getFullYear());
      }
    });
    return {
      lifetime,
      largest,
      count: memberGifts.length,
      years: giftYears.size,
      lastGiftDate: memberGifts[0]?.gift_date || null,
    };
  }, [memberGifts]);

  const contactLogs = selectedMember?.contactLog || [];
  const logsLast12Months = useMemo(() => {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 12);
    return contactLogs.filter((log) => {
      const d = log.logDate || log.log_date;
      return d && new Date(d) >= cutoff;
    }).length;
  }, [contactLogs]);

  const confidence = useMemo(
    () =>
      computeCapacityConfidence({
        member: selectedMember,
        capacity: selectedMemberCapacity,
        contactLogs,
        gifts: memberGifts,
        engagementScore,
        wealthProfile,
        weights: capacityWeights,
        chapterHasWealthData,
      }),
    [
      selectedMember,
      selectedMemberCapacity,
      contactLogs,
      memberGifts,
      engagementScore,
      wealthProfile,
      capacityWeights,
      chapterHasWealthData,
    ]
  );

  const handleSave = async (form) => {
    setSaving(true);
    try {
      await upsertSelectedMemberCapacity(form);
      showMessage("Capacity assessment saved");
      setEditing(false);
    } catch (err) {
      showMessage("Could not save assessment", "error");
    } finally {
      setSaving(false);
    }
  };

  const band = getCapacityBand(selectedMemberCapacity?.capacityBand);

  return (
    <>
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ── Headline Card ──────────────────────────────────────── */}
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 24,
            flexWrap: "wrap",
          }}
        >
          <ConfidenceDial score={confidence.score} palette={palette} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 6,
              }}
            >
              <Target size={ICON_SIZES.md} color={palette.accent1} />
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: palette.text3,
                  letterSpacing: 0.5,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                ESTIMATED CAPACITY
                <InfoBubble
                  size={ICON_SIZES.xs}
                  text="The 5-year giving capacity band you believe this donor could reach if maximally inclined. Enter your assessment below — your judgment counts for 20% of the confidence score."
                />
              </div>
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 800,
                color: palette.text1,
                lineHeight: 1.1,
                marginBottom: 8,
              }}
            >
              {band ? band.label : "Not yet estimated"}
            </div>
            {confidence.gaps.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 6,
                  padding: 10,
                  borderRadius: 6,
                  background: "#fef3c7",
                  border: "1px solid #fde68a",
                  fontSize: 12,
                  color: "#78350f",
                  lineHeight: 1.5,
                }}
              >
                <Sparkles size={ICON_SIZES.sm} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>
                  <strong>Next move to strengthen this call:</strong>{" "}
                  {confidence.gaps[0].missing}
                </span>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ── Director's Assessment ──────────────────────────────── */}
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 14,
          }}
        >
          <Edit2 size={ICON_SIZES.sm} color={palette.accent1} />
          <div
            style={{
              fontSize: FONT_SIZES.base,
              fontWeight: 700,
              color: palette.text1,
            }}
          >
            Director's Assessment
          </div>
          <InfoBubble
            size={ICON_SIZES.sm}
            text="Your direct judgment of this donor's capacity. This is the 20% of the confidence model the system can't compute on its own — a well-filled assessment will noticeably raise the overall score."
          />
        </div>
        {editing ? (
          <AssessmentEditor
            capacity={selectedMemberCapacity}
            onSave={handleSave}
            onCancel={() => setEditing(false)}
            palette={palette}
            saving={saving}
          />
        ) : (
          <AssessmentDisplay
            capacity={selectedMemberCapacity}
            onEdit={() => setEditing(true)}
            palette={palette}
          />
        )}
      </Card>

      {/* ── Observed Signals ───────────────────────────────────── */}
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 14,
          }}
        >
          <TrendingUp size={ICON_SIZES.sm} color={palette.accent1} />
          <div
            style={{
              fontSize: FONT_SIZES.base,
              fontWeight: 700,
              color: palette.text1,
            }}
          >
            What the System Sees
          </div>
          <span
            style={{
              fontSize: 11,
              color: palette.text3,
              fontWeight: 400,
              marginLeft: 4,
            }}
          >
            (drawn automatically from this donor's record)
          </span>
          <InfoBubble
            size={ICON_SIZES.sm}
            text="These stats are computed from this donor's gifts, contact logs, and engagement score — they update automatically whenever new data is logged. Use them to inform your capacity band above."
          />
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <StatTile
            label="LIFETIME GIVING"
            value={formatMoney(giftStats.lifetime)}
            palette={palette}
          />
          <StatTile
            label="LARGEST SINGLE GIFT"
            value={formatMoney(giftStats.largest)}
            palette={palette}
          />
          <StatTile
            label="GIFTS"
            value={String(giftStats.count)}
            palette={palette}
          />
          <StatTile
            label="YEARS GIVING"
            value={String(giftStats.years)}
            palette={palette}
          />
          <StatTile
            label="CONTACTS (12 MO)"
            value={String(logsLast12Months)}
            palette={palette}
          />
          <StatTile
            label="ENGAGEMENT"
            value={
              engagementScore != null ? `${Math.round(engagementScore)}` : "—"
            }
            palette={palette}
          />
        </div>
      </Card>

      {/* ── Wealth Evidence: existing data OR research prompt ──── */}
      {wealthProfile ? (
        <WealthProfileBlock
          profile={wealthProfile}
          palette={palette}
          onEdit={() => setResearchOpen(true)}
        />
      ) : (
        <ResearchPromptBlock
          palette={palette}
          onStart={() => setResearchOpen(true)}
        />
      )}

      {/* ── Confidence Breakdown ───────────────────────────────── */}
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 14,
          }}
        >
          <CheckCircle size={ICON_SIZES.sm} color={palette.accent1} />
          <div
            style={{
              fontSize: FONT_SIZES.base,
              fontWeight: 700,
              color: palette.text1,
            }}
          >
            Confidence Breakdown
          </div>
          <InfoBubble
            size={ICON_SIZES.sm}
            text="Six weighted signals combine into the overall confidence score at the top of this tab. Amber alerts on any component tell you the single best move to raise that signal. Admins can tune the weights in Settings → Scoring."
          />
        </div>
        {confidence.components
          .filter((c) => c.included)
          .map((component) => (
            <ComponentBar
              key={component.key}
              component={component}
              palette={palette}
            />
          ))}
        {!confidence.wealthDataAvailable && (
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              marginTop: 12,
              padding: 10,
              borderRadius: 6,
              background: `${palette.accent1}08`,
              border: `1px dashed ${palette.cardBorder}`,
              fontSize: 12,
              color: palette.text2,
              lineHeight: 1.5,
            }}
          >
            <Info size={ICON_SIZES.sm} style={{ marginTop: 2, flexShrink: 0, color: palette.accent1 }} />
            <span>
              External wealth data isn't in use for this chapter, so that
              component has been rebalanced across the others. Import a
              DonorSearch CSV (coming soon) to activate it.
            </span>
          </div>
        )}
      </Card>

      {/* ── Next Action Row ────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        {onLogContact && (
          <ActionButton
            label="Log Contact"
            variant="secondary"
            onClick={onLogContact}
          />
        )}
        <ActionButton
          label={selectedMemberCapacity ? "Adjust Estimate" : "Enter Assessment"}
          icon={Edit2}
          onClick={() => setEditing(true)}
        />
      </div>
    </div>
    {researchOpen && selectedMember && (
      <ManualResearchModal
        member={selectedMember}
        existing={wealthProfile}
        onClose={() => setResearchOpen(false)}
      />
    )}
    </>
  );
};
